/** In-progress workout UI state (cleared when the session is finished). */

const STORAGE_PREFIX = 'tt_workout_session_'

export interface WorkoutExerciseDraft {
  comment: string
  setLogs: Array<{ weight: string; reps: string }>
}

export interface WorkoutSessionUiCache {
  exerciseIndex: number
  drafts: Record<string, WorkoutExerciseDraft>
  updatedAt: number
}

function storageKey(sessionId: number): string {
  return `${STORAGE_PREFIX}${sessionId}`
}

function readRaw(sessionId: number): WorkoutSessionUiCache | null {
  try {
    const raw = sessionStorage.getItem(storageKey(sessionId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as WorkoutSessionUiCache
    if (typeof parsed.exerciseIndex !== 'number' || !parsed.drafts || typeof parsed !== 'object') {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeRaw(sessionId: number, cache: WorkoutSessionUiCache): void {
  try {
    sessionStorage.setItem(storageKey(sessionId), JSON.stringify(cache))
  } catch {
    // Quota or private mode — ignore
  }
}

export function readWorkoutSessionCache(sessionId: number): WorkoutSessionUiCache | null {
  return readRaw(sessionId)
}

export function clearWorkoutSessionCache(sessionId: number): void {
  try {
    sessionStorage.removeItem(storageKey(sessionId))
  } catch {
    // ignore
  }
}

export function setWorkoutExerciseIndex(sessionId: number, exerciseIndex: number): void {
  const prev = readRaw(sessionId) ?? { exerciseIndex: 0, drafts: {}, updatedAt: Date.now() }
  writeRaw(sessionId, {
    ...prev,
    exerciseIndex,
    updatedAt: Date.now(),
  })
}

export function setWorkoutExerciseDraft(
  sessionId: number,
  logId: number,
  draft: WorkoutExerciseDraft,
): void {
  const prev = readRaw(sessionId) ?? { exerciseIndex: 0, drafts: {}, updatedAt: Date.now() }
  writeRaw(sessionId, {
    ...prev,
    drafts: {
      ...prev.drafts,
      [String(logId)]: {
        comment: draft.comment,
        setLogs: draft.setLogs,
      },
    },
    updatedAt: Date.now(),
  })
}

export function getWorkoutExerciseDraft(
  sessionId: number,
  logId: number,
): WorkoutExerciseDraft | null {
  const cache = readRaw(sessionId)
  if (!cache) return null
  return cache.drafts[String(logId)] ?? null
}

export function getWorkoutExerciseIndex(sessionId: number): number | null {
  const cache = readRaw(sessionId)
  if (!cache) return null
  return cache.exerciseIndex
}

export function clampExerciseIndex(index: number, exerciseCount: number): number {
  if (exerciseCount <= 0) return 0
  return Math.min(Math.max(0, index), exerciseCount - 1)
}
